import { Store, useStore } from '@tanstack/react-store'
import { useEffect, useMemo, useState } from 'react'
import { getDateRangeForPreset, type ReceivedAtPreset, type CustomDateRange } from '@/lib/date-filter-utils'
import type { InquiryStageEnum } from '@/gen/cortado/v1/settings_pb'
import { isActiveLeadStage } from '@/lib/leads'
import type { StageLeadsFilterOptions } from './leads.store'

const LOCAL_STORAGE_KEY = 'cortado_leads_filters'

/** Sentinel value in selectedAssignees meaning "include unassigned leads". */
export const UNASSIGNED_ASSIGNEE_FILTER = '__unassigned__'

/** Which stage columns the leads page shows: active-only or every stage. */
export type LeadsStageScope = 'active' | 'all'

interface LeadsFiltersState {
  // Tab selection
  selectedTab: 'kanban' | 'table'

  // Shared filters (persisted)
  receivedDateFilter: ReceivedAtPreset | null
  receivedDateRange: CustomDateRange | null  // Custom From/To date range option
  eventDateRange: CustomDateRange | null
  /** Organization event type keys (e.g. "Wedding"). */
  selectedEventTypes: string[]
  selectedStages: number[]
  selectedSources: string[]
  selectedAssignees: string[]
  stageScope: LeadsStageScope
  /** When true, archived leads are included in every leads query. */
  includeArchived: boolean
}

const defaultState: LeadsFiltersState = {
  selectedTab: 'kanban',
  receivedDateFilter: null,
  receivedDateRange: null,
  eventDateRange: null,
  selectedEventTypes: [],
  selectedStages: [],
  selectedSources: [],
  selectedAssignees: [],
  stageScope: 'active',
  includeArchived: false,
}

function loadFromStorage(): LeadsFiltersState {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Restore Date objects for eventDateRange
      if (parsed.eventDateRange?.from) {
        parsed.eventDateRange.from = new Date(parsed.eventDateRange.from)
      }
      if (parsed.eventDateRange?.to) {
        parsed.eventDateRange.to = new Date(parsed.eventDateRange.to)
      }
      // Restore Date objects for receivedDateRange
      if (parsed.receivedDateRange?.from) {
        parsed.receivedDateRange.from = new Date(parsed.receivedDateRange.from)
      }
      if (parsed.receivedDateRange?.to) {
        parsed.receivedDateRange.to = new Date(parsed.receivedDateRange.to)
      }
      // Migrate old receivedSinceDate to receivedDateRange (backward compatibility)
      if (parsed.receivedSinceDate && !parsed.receivedDateRange) {
        parsed.receivedDateRange = { from: new Date(parsed.receivedSinceDate) }
        delete parsed.receivedSinceDate
      }
      // Event types used to be numeric proto enum values; they are now
      // org-scoped key strings. Drop any legacy numeric entries.
      if (Array.isArray(parsed.selectedEventTypes)) {
        parsed.selectedEventTypes = parsed.selectedEventTypes.filter(
          (type: unknown) => typeof type === 'string'
        )
      }
      if (Array.isArray(parsed.selectedAssignees)) {
        parsed.selectedAssignees = parsed.selectedAssignees.filter(
          (assignee: unknown) => typeof assignee === 'string'
        )
      }
      // Drop invalid/legacy stageScope values so the default applies
      if (parsed.stageScope !== 'active' && parsed.stageScope !== 'all') {
        delete parsed.stageScope
      }
      if (typeof parsed.includeArchived !== 'boolean') {
        delete parsed.includeArchived
      }
      return { ...defaultState, ...parsed }
    }
  } catch (e) {
    console.warn('Failed to load leads filters:', e)
  }
  return defaultState
}

function saveToStorage(state: LeadsFiltersState) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state))
  } catch (e) {
    console.warn('Failed to save leads filters:', e)
  }
}

export const leadsFiltersStore = new Store<LeadsFiltersState>(loadFromStorage())

/**
 * Ephemeral (NOT persisted) full-text search over lead contact name/email,
 * shared by the kanban and table views via {@link useLeadsStageFilterOptions}.
 */
const leadsSearchStore = new Store<string>('')

export function useLeadsSearchQuery(): string {
  return useStore(leadsSearchStore)
}

export const leadsFiltersActions = {
  setSelectedTab: (tab: 'kanban' | 'table') => {
    leadsFiltersStore.setState((state) => {
      const newState = { ...state, selectedTab: tab }
      saveToStorage(newState)
      return newState
    })
  },

  setReceivedDateFilter: (filter: ReceivedAtPreset | null) => {
    leadsFiltersStore.setState((state) => {
      // Clear custom date range when preset is selected
      const newState = { ...state, receivedDateFilter: filter, receivedDateRange: null }
      saveToStorage(newState)
      return newState
    })
  },

  setReceivedDateRange: (range: CustomDateRange | null) => {
    leadsFiltersStore.setState((state) => {
      // Clear preset when custom date range is selected
      const newState = { ...state, receivedDateRange: range, receivedDateFilter: null }
      saveToStorage(newState)
      return newState
    })
  },

  setEventDateRange: (range: CustomDateRange | null) => {
    leadsFiltersStore.setState((state) => {
      const newState = { ...state, eventDateRange: range }
      saveToStorage(newState)
      return newState
    })
  },

  setSelectedEventTypes: (types: string[]) => {
    leadsFiltersStore.setState((state) => {
      const newState = { ...state, selectedEventTypes: types }
      saveToStorage(newState)
      return newState
    })
  },

  setSelectedStages: (stages: number[]) => {
    leadsFiltersStore.setState((state) => {
      const newState = { ...state, selectedStages: stages }
      saveToStorage(newState)
      return newState
    })
  },

  setSelectedSources: (sources: string[]) => {
    leadsFiltersStore.setState((state) => {
      const newState = { ...state, selectedSources: sources }
      saveToStorage(newState)
      return newState
    })
  },

  setSelectedAssignees: (assignees: string[]) => {
    leadsFiltersStore.setState((state) => {
      const newState = { ...state, selectedAssignees: assignees }
      saveToStorage(newState)
      return newState
    })
  },

  setSearchQuery: (query: string) => {
    leadsSearchStore.setState(() => query)
  },

  setStageScope: (scope: LeadsStageScope) => {
    leadsFiltersStore.setState((state) => {
      const newState = { ...state, stageScope: scope }
      saveToStorage(newState)
      return newState
    })
  },

  setIncludeArchived: (includeArchived: boolean) => {
    leadsFiltersStore.setState((state) => {
      const newState = { ...state, includeArchived }
      saveToStorage(newState)
      return newState
    })
  },

  resetFilters: () => {
    leadsFiltersStore.setState((state) => {
      const newState = {
        ...state,
        receivedDateFilter: null,
        receivedDateRange: null,
        eventDateRange: null,
        selectedEventTypes: [],
        selectedStages: [],
        selectedSources: [],
        selectedAssignees: [],
        stageScope: 'active' as LeadsStageScope,
        includeArchived: false,
      }
      saveToStorage(newState)
      return newState
    })
    leadsSearchStore.setState(() => '')
  },
}

// Hooks for granular subscriptions
export function useLeadsSelectedTab() {
  return useStore(leadsFiltersStore, (s) => s.selectedTab)
}

export function useLeadsReceivedDateFilter() {
  return useStore(leadsFiltersStore, (s) => s.receivedDateFilter)
}

export function useLeadsReceivedDateRange() {
  return useStore(leadsFiltersStore, (s) => s.receivedDateRange)
}

export function useLeadsEventDateRange() {
  return useStore(leadsFiltersStore, (s) => s.eventDateRange)
}

export function useLeadsSelectedEventTypes() {
  return useStore(leadsFiltersStore, (s) => s.selectedEventTypes)
}

export function useLeadsSelectedStages() {
  return useStore(leadsFiltersStore, (s) => s.selectedStages)
}

export function useLeadsSelectedSources() {
  return useStore(leadsFiltersStore, (s) => s.selectedSources)
}

export function useLeadsSelectedAssignees() {
  return useStore(leadsFiltersStore, (s) => s.selectedAssignees)
}

export function useLeadsStageScope(): LeadsStageScope {
  return useStore(leadsFiltersStore, (s) => s.stageScope)
}

/**
 * Whether archived leads are shown. Like {@link useLeadsStageScope} this is a
 * boolean scope toggle rather than a value filter — it widens what the server
 * returns instead of narrowing it, and (unlike the Active/All stage scope) it
 * is never overridden by another filter.
 */
export function useLeadsIncludeArchived(): boolean {
  return useStore(leadsFiltersStore, (s) => s.includeArchived)
}

export function useLeadsFilters() {
  return useStore(leadsFiltersStore)
}

/**
 * Convert a received date preset to a date range
 */
function presetToDateRange(preset: ReceivedAtPreset | null): CustomDateRange | undefined {
  if (!preset) return undefined
  const range = getDateRangeForPreset(preset)
  return { from: range.start, to: range.end }
}

export function getLeadsFilterOptions(state: LeadsFiltersState = leadsFiltersStore.state) {
  const dateRange = state.receivedDateRange || presetToDateRange(state.receivedDateFilter)
  const assignedTo = state.selectedAssignees.filter((id) => id !== UNASSIGNED_ASSIGNEE_FILTER)
  const includeUnassigned = state.selectedAssignees.includes(UNASSIGNED_ASSIGNEE_FILTER)

  return {
    stages: state.selectedStages.length > 0 ? state.selectedStages as InquiryStageEnum[] : undefined,
    eventTypes: state.selectedEventTypes.length > 0 ? state.selectedEventTypes : undefined,
    sourceChannels: state.selectedSources.length > 0 ? state.selectedSources : undefined,
    assignedTo: assignedTo.length > 0 ? assignedTo : undefined,
    includeUnassigned: includeUnassigned || undefined,
    since: dateRange?.from,
    createdBefore: dateRange?.to,
    eventDateStart: state.eventDateRange?.from,
    eventDateEnd: state.eventDateRange?.to,
    includeArchived: state.includeArchived || undefined,
  }
}

/**
 * Hook that converts filter store state to API-compatible options for useLeadsList
 */
export function useLeadsFilterOptions() {
  const receivedDateFilter = useLeadsReceivedDateFilter()
  const receivedDateRange = useLeadsReceivedDateRange()
  const eventDateRange = useLeadsEventDateRange()
  const selectedEventTypes = useLeadsSelectedEventTypes()
  const selectedSources = useLeadsSelectedSources()
  const selectedAssignees = useLeadsSelectedAssignees()
  const selectedStages = useLeadsSelectedStages()
  const includeArchived = useLeadsIncludeArchived()

  return useMemo(() => {
    return getLeadsFilterOptions({
      ...leadsFiltersStore.state,
      receivedDateFilter,
      receivedDateRange,
      eventDateRange,
      selectedEventTypes,
      selectedSources,
      selectedAssignees,
      selectedStages,
      includeArchived,
    })
  }, [receivedDateFilter, receivedDateRange, eventDateRange, selectedEventTypes, selectedSources, selectedAssignees, selectedStages, includeArchived])
}

/**
 * Hook that converts filter store state to API-compatible options shared by
 * every per-stage leads query ({@link getLeadsFilterOptions} minus `stages` —
 * each stage column pins its own single-stage filter).
 */
export function useLeadsStageFilterOptions(): StageLeadsFilterOptions {
  const receivedDateFilter = useLeadsReceivedDateFilter()
  const receivedDateRange = useLeadsReceivedDateRange()
  const eventDateRange = useLeadsEventDateRange()
  const selectedEventTypes = useLeadsSelectedEventTypes()
  const selectedSources = useLeadsSelectedSources()
  const selectedAssignees = useLeadsSelectedAssignees()
  const includeArchived = useLeadsIncludeArchived()
  const searchQuery = useLeadsSearchQuery()
  // Debounce so the server is hit as the user pauses typing, not on every keystroke.
  const debouncedSearch = useDebouncedValue(searchQuery.trim(), 300)

  return useMemo(() => {
    const dateRange = receivedDateRange || presetToDateRange(receivedDateFilter)
    const assignedTo = selectedAssignees.filter((id) => id !== UNASSIGNED_ASSIGNEE_FILTER)
    const includeUnassigned = selectedAssignees.includes(UNASSIGNED_ASSIGNEE_FILTER)

    return {
      eventTypes: selectedEventTypes.length > 0 ? selectedEventTypes : undefined,
      since: dateRange?.from,
      createdBefore: dateRange?.to,
      eventDateStart: eventDateRange?.from,
      eventDateEnd: eventDateRange?.to,
      search: debouncedSearch || undefined,
      sourceChannels: selectedSources.length > 0 ? selectedSources : undefined,
      assignedTo: assignedTo.length > 0 ? assignedTo : undefined,
      includeUnassigned: includeUnassigned || undefined,
      // Left undefined (not `false`) when off so the query key — and therefore
      // every existing lead list cache — is unchanged until archived is asked for.
      includeArchived: includeArchived || undefined,
    }
  }, [receivedDateFilter, receivedDateRange, eventDateRange, selectedEventTypes, selectedSources, selectedAssignees, debouncedSearch, includeArchived])
}

/**
 * Which stage columns the leads page should fetch and render, preserving the
 * order of `allStages` (settings order). An explicit stage filter
 * (`selectedStages`) overrides the Active/All scope toggle.
 */
export function useLeadsVisibleStages(allStages: InquiryStageEnum[]): {
  visibleStages: InquiryStageEnum[]
  scope: LeadsStageScope
  isScopeOverridden: boolean
} {
  const scope = useLeadsStageScope()
  const selectedStages = useLeadsSelectedStages()

  return useMemo(() => {
    const isScopeOverridden = selectedStages.length > 0
    let visibleStages: InquiryStageEnum[]
    if (isScopeOverridden) {
      visibleStages = allStages.filter((stage) => selectedStages.includes(stage))
    } else if (scope === 'active') {
      visibleStages = allStages.filter(isActiveLeadStage)
    } else {
      visibleStages = allStages
    }
    return { visibleStages, scope, isScopeOverridden }
  }, [allStages, scope, selectedStages])
}

/** Returns `value` after it has stopped changing for `delayMs` milliseconds. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedValue(value), delayMs)
    return () => clearTimeout(timeoutId)
  }, [delayMs, value])

  return debouncedValue
}
